// frontend/src/components/dashboard/ActivityPlanner.jsx
import React, { useState, useEffect } from 'react';
import { PlusIcon, CalendarIcon, ClockIcon, MapPinIcon, CurrencyRupeeIcon, UsersIcon } from '@heroicons/react/24/outline';
import groupDashboardService from '../../services/groupDashboardService';
import Button from '../common/Button';

const ActivityPlanner = ({ groupId, membership, user }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [newActivity, setNewActivity] = useState({
    title: '',
    description: '',
    date: '',
    time: '09:00',
    location: '',
    cost: '',
    category: 'sightseeing'
  });
  
  useEffect(() => {
    loadActivities();
  }, [groupId]);
  
  const loadActivities = async () => {
    try {
      setLoading(true);
      const activitiesData = await groupDashboardService.getActivities(groupId);
      setActivities(activitiesData);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      await groupDashboardService.addActivity(groupId, newActivity);
      
      setNewActivity({
        title: '',
        description: '',
        date: '',
        time: '09:00',
        location: '',
        cost: '',
        category: 'sightseeing'
      });
      setShowAddForm(false);
      loadActivities();
    } catch (error) {
      console.error('Failed to add activity:', error);
    }
  };
  
  const categories = [
    { id: 'sightseeing', name: 'Sightseeing', color: 'bg-blue-100 text-blue-800' },
    { id: 'adventure', name: 'Adventure', color: 'bg-red-100 text-red-800' },
    { id: 'food', name: 'Food & Dining', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'shopping', name: 'Shopping', color: 'bg-pink-100 text-pink-800' },
    { id: 'cultural', name: 'Cultural', color: 'bg-purple-100 text-purple-800' },
    { id: 'relaxation', name: 'Relaxation', color: 'bg-green-100 text-green-800' },
    { id: 'other', name: 'Other', color: 'bg-gray-100 text-gray-800' }
  ];
  
  if (loading) {
    return <div className="text-center py-8">Loading activities...</div>;
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Activity Planner</h2>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Activity
        </Button>
      </div>
      
      {/* Add Activity Form */}
      {showAddForm && (
        <div className="mb-6 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100">
          <h3 className="font-bold text-gray-800 mb-4 text-lg">Plan New Activity</h3>
          
          <form onSubmit={handleAddActivity} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Title
                </label>
                <input
                  type="text"
                  value={newActivity.title}
                  onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Beach Visit"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={newActivity.category}
                  onChange={(e) => setNewActivity({ ...newActivity, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={newActivity.date}
                  onChange={(e) => setNewActivity({ ...newActivity, date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <input
                  type="time"
                  value={newActivity.time}
                  onChange={(e) => setNewActivity({ ...newActivity, time: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={newActivity.location}
                  onChange={(e) => setNewActivity({ ...newActivity, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Main Beach"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Cost (₹)
                </label>
                <input
                  type="number"
                  value={newActivity.cost}
                  onChange={(e) => setNewActivity({ ...newActivity, cost: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="0"
                  step="0.01"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={newActivity.description}
                onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                rows="3"
                placeholder="Describe the activity..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-lg"
              >
                Add Activity
              </Button>
            </div>
          </form>
        </div>
      )}
      
      {/* Activities List */}
      <div className="space-y-4">
        {activities.length === 0 ? (
          <div className="text-center py-12">
            <CalendarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Activities Planned</h3>
            <p className="text-gray-600 mb-6">Start planning your trip activities!</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-lg"
            >
              Plan First Activity
            </Button>
          </div>
        ) : (
          activities.map((activity) => (
            <div key={activity._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center mb-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${categories.find(c => c.id === activity.category)?.color}`}>
                      {categories.find(c => c.id === activity.category)?.name}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">{activity.title}</h3>
                  {activity.description && (
                    <p className="text-gray-600 mt-1">{activity.description}</p>
                  )}
                </div>
                
                {activity.cost > 0 && (
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Estimated Cost</div>
                    <div className="text-2xl font-bold text-green-600">₹{activity.cost}</div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                  <CalendarIcon className="h-5 w-5 text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-medium">{new Date(activity.date).toLocaleDateString()}</p>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                  <ClockIcon className="h-5 w-5 text-purple-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-medium">{activity.time}</p>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-green-50 rounded-lg">
                  <MapPinIcon className="h-5 w-5 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium">{activity.location}</p>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
                  <UsersIcon className="h-5 w-5 text-yellow-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Participants</p>
                    <p className="font-medium">{activity.participants?.length || 1}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-2">
                    {activity.createdBy?.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">{activity.createdBy?.name}</p>
                    <p className="text-xs text-gray-500">
                      Added {new Date(activity.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  activity.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  activity.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                  activity.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {activity.status?.toUpperCase()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityPlanner;